import React, { useState, useEffect } from 'react';
import { Button, FormGroup, Label, Input } from 'reactstrap';
import Select from 'react-select';
import queryString from 'query-string';
import { programRequestsApi } from '../api/programRequestsApi';
import { InstitutionField } from '../shared/InstitutionField';
import { ProgramSelector } from '../shared/ProgramSelector';
import { ProgramSummaryCard } from '../shared/ProgramSummaryCard';
import {
    AdditionalCommentsField,
    commentsLengthError,
} from '../shared/AdditionalCommentsField';
import { RadioCheckGroup } from 'components/academic-affair-report/components/RadioCheckGroup';
import {
    createSplitProgramModel,
    splitProgramFromDetails,
} from './splitProgramModel';
import { createSplitProgramFlow } from './createSplitProgramFlow';

const DELIVERY_MODE_OPTIONS = [
    { value: 1, label: 'On Campus Only' },
    { value: 2, label: 'Online Only' },
    { value: 3, label: 'On Campus & Online' },
];

const ON_CAMPUS_ONLINE_NATURE_OPTIONS = [
    { value: 1, label: '100% Online' },
    { value: 2, label: 'Blended' },
];

const numberOrNull = value =>
    value === null || value === undefined || value === '' ? null : Number(value);

const normalizeNumber = value =>
    value === '' || value === null || value === undefined ? null : Number(value);

const creditHoursError = form => {
    const minimum = normalizeNumber(form.creditMin);
    const maximum = normalizeNumber(form.creditMax);
    if (minimum === null || maximum === null) {
        return 'Enter both Minimum and Maximum credit hours.';
    }
    if (minimum <= 0 || maximum <= 0) {
        return 'Credit Hours Minimum and Maximum must be greater than 0.';
    }
    if (minimum > maximum) {
        return 'Credit Hours Maximum must be greater than or equal to Minimum.';
    }
    return null;
};

function validateNewProgram(prog) {
    const errors = {};
    const deliveryMode = numberOrNull(prog.deliveryMode);
    if (!prog.programName?.trim()) errors.programName = 'Program Name is required.';
    if (!prog.cipCode) errors.cipCode = 'CIP Code is required.';
    if (deliveryMode === null) errors.deliveryMode = 'Delivery Mode is required.';
    if (deliveryMode === 3 && prog.onlineNature === null) errors.onlineNature = 'Nature of Online Program is required.';
    const chErr = creditHoursError(prog);
    if (chErr) errors.creditHours = chErr;
    return errors;
}

// Derive the auto-set onlineNature for modes 1 and 2; use existing value for mode 3.
// For mode 3 a nature of 0 (None) is invalid, so fall back to null to force the user to choose.
function deriveNature(modeId, existingNatureId) {
    if (modeId === 1) return 0;                                              // campus only → None
    if (modeId === 2) return 1;                                              // online only → 100% Online
    return (existingNatureId != null && existingNatureId !== 0)              // mode 3 → valid existing value
        ? existingNatureId
        : null;                                                              // or force user to choose
}

function buildPrePopulated(sourceProgram) {
    return {
        programName: '',
        cipCode: sourceProgram.cipSixDigitCode ?? null,
        degreeLevel: sourceProgram.degreeLevelCode ?? null,
        deliveryMode: numberOrNull(sourceProgram.programDistanceEdId),
        onlineNature: deriveNature(
            numberOrNull(sourceProgram.programDistanceEdId),
            sourceProgram.programDistanceEdOnlineId,
        ),
        creditMin: sourceProgram.programCreditReqMin != null
            ? String(sourceProgram.programCreditReqMin)
            : '',
        creditMax: sourceProgram.programCreditReqMax != null
            ? String(sourceProgram.programCreditReqMax)
            : '',
    };
}

export const SplitProgramForm = ({
    onBack,
    onSuccess,
    institutionOptions: parentInstitutionOptions = [],
    programsByInstitution = {},
    maximumAdditionalCommentsLength,
    initialModel,
    submitFlow = createSplitProgramFlow,
    mode = 'create',
    requestDetails,
}) => {
    const [form, setForm] = useState(
        () => initialModel ?? createSplitProgramModel()
    );
    const [institutionOptions, setInstitutionOptions] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [loadingPrograms, setLoadingPrograms] = useState(false);
    const [cipOptions, setCipOptions] = useState([]);
    const [degreeLevelOptions, setDegreeLevelOptions] = useState([]);
    const [loadingCips, setLoadingCips] = useState(true);

    const [newPrograms, setNewPrograms] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);

    const [programsReady, setProgramsReady] = useState(mode !== 'edit');
    const [splitRequestsReady, setSplitRequestsReady] = useState(mode !== 'edit');

    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    // Load institutions, CIP codes, and degree levels once on mount.
    useEffect(() => {
        if (parentInstitutionOptions.length > 0) {
            setInstitutionOptions(parentInstitutionOptions);
        } else {
            programRequestsApi.getInstitutions().then(res => {
                const mapped = (res ?? []).map(x => ({ value: x.label, label: x.value }));
                setInstitutionOptions(mapped);
            });
        }

        setLoadingCips(true);
        programRequestsApi.getCipCodes().then(res => {
            const mapped = (res ?? []).map(x => ({
                value: x.cipCode,
                label: `${x.cipCode} - ${x.cipName}`,
            }));
            setCipOptions(mapped);
            setLoadingCips(false);
        });

        programRequestsApi.getDegreeLevels().then(res => {
            const mapped = (res ?? []).map(x => ({ value: x.label, label: x.value }));
            setDegreeLevelOptions(mapped);
        });
    }, []);

    // Auto-select when only one institution is available.
    useEffect(() => {
        if (institutionOptions.length === 1 && form.institutionId === null) {
            setForm(f => ({ ...f, institutionId: institutionOptions[0].value }));
        }
    }, [institutionOptions]);

    // Fetch programs when institution changes.
    useEffect(() => {
        if (!form.institutionId) {
            setPrograms([]);
            setForm(f => ({ ...f, sourceProgramId: null }));
            setNewPrograms([]);
            setActiveIndex(0);
            setProgramsReady(true);
            return;
        }
        if (mode !== 'edit') {
            setForm(f => ({ ...f, sourceProgramId: null }));
            setNewPrograms([]);
            setActiveIndex(0);
        }

        const cached = programsByInstitution[form.institutionId];
        if (Array.isArray(cached) && cached.length > 0) {
            setPrograms(cached);
            setProgramsReady(true);
            return;
        }

        setLoadingPrograms(true);
        const query = queryString.stringify(
            { institutions: form.institutionId },
            { arrayFormat: 'separator', skipEmptyString: true },
        );
        programRequestsApi.getProgramsForInstitution(query)
            .then(res => setPrograms(res?.programs?.flat() ?? []))
            .finally(() => {
                setLoadingPrograms(false);
                setProgramsReady(true);
            });
    }, [form.institutionId, mode]);

    // Pre-populate new programs when source program selection changes.
    useEffect(() => {
        if (mode === 'edit') return;
        if (!form.sourceProgramId) {
            setNewPrograms([]);
            setActiveIndex(0);
            return;
        }
        const src = programs.find(p => p.programId === form.sourceProgramId);
        if (src) {
            setNewPrograms([buildPrePopulated(src)]);
            setActiveIndex(0);
        }
    }, [form.sourceProgramId, mode]);

    useEffect(() => {
        if (mode !== 'edit') return;
        if (!requestDetails) {
            setSplitRequestsReady(true);
            return;
        }
        const requestId = requestDetails.requestID ?? requestDetails.requestId;
        if (!requestId) {
            setSplitRequestsReady(true);
            return;
        }

        programRequestsApi.getSplitProgramRequests(requestId).then(response => {
            setNewPrograms((response ?? []).map(splitProgramFromDetails));
            setActiveIndex(0);
            setSplitRequestsReady(true);
        });
    }, [mode, requestDetails]);

    const setField = (key, value) => setForm(f => ({ ...f, [key]: value }));

    const activeProg = newPrograms[activeIndex] ?? null;

    const updateActiveProg = (key, value) => {
        setNewPrograms(progs =>
            progs.map((p, i) => (i === activeIndex ? { ...p, [key]: value } : p)),
        );
    };

    const handleDeliveryModeChange = obj => {
        const mode = numberOrNull(obj.deliveryMode);
        const nature = mode === 1 ? 0 : mode === 2 ? 1 : null;
        setNewPrograms(progs =>
            progs.map((p, i) =>
                i === activeIndex ? { ...p, deliveryMode: mode, onlineNature: nature } : p,
            ),
        );
    };

    const handleNatureChange = obj => {
        const raw = obj.onlineNature;
        updateActiveProg('onlineNature', raw === '' ? null : raw);
    };

    const addNewProgram = () => {
        const src = programs.find(p => p.programId === form.sourceProgramId);
        const newProg = src ? buildPrePopulated(src) : {
            programName: '', cipCode: null, degreeLevel: null,
            deliveryMode: null, onlineNature: null, creditMin: '', creditMax: '',
        };
        setNewPrograms(progs => [...progs, newProg]);
        setActiveIndex(newPrograms.length); // will be the last index after append
    };

    const removeActiveProgram = () => {
        if (newPrograms.length <= 1) return;
        setNewPrograms(progs => progs.filter((_, i) => i !== activeIndex));
        setActiveIndex(idx => Math.min(idx, newPrograms.length - 2));
    };

    const handleSubmit = () => {
        setSubmitted(true);
        setSubmitError(null);

        if (
            !form.institutionId ||
            !form.sourceProgramId ||
            newPrograms.length === 0 ||
            commentsLengthError(form.comments, maximumAdditionalCommentsLength)
        ) return;

        // Navigate to the first program that has validation errors.
        const firstBadIdx = newPrograms.findIndex(p => Object.keys(validateNewProgram(p)).length > 0);
        if (firstBadIdx !== -1) {
            setActiveIndex(firstBadIdx);
            return;
        }

        setSubmitting(true);

        const onError = err => {
            const message =
                err?.response?.data?.message ?? 'An unexpected error occurred. Please try again.';
            setSubmitError(message);
            setSubmitting(false);
        };

        submitFlow({
            form,
            newPrograms,
            details: requestDetails,
            onError,
        }).then(result => {
            setSubmitting(false);
            if (!result) return;
            onSuccess();
        });
    };

    const sourceProgram = programs.find(p => p.programId === form.sourceProgramId) ?? null;

    const activeErrors = submitted && activeProg ? validateNewProgram(activeProg) : {};
    const activeDeliveryMode = activeProg ? numberOrNull(activeProg.deliveryMode) : null;

    // Count programs that have errors (for the multi-program error hint).
    const programsWithErrors = submitted
        ? newPrograms.reduce((acc, p, i) => {
            return Object.keys(validateNewProgram(p)).length > 0 ? [...acc, i + 1] : acc;
        }, [])
        : [];

    const err = msg => <p className="text-danger mt-1 small">{msg}</p>;

    if (mode === 'edit' && !(programsReady && splitRequestsReady)) {
        return (
            <p className="text-muted small text-center py-4 mb-0">
                Loading request details…
            </p>
        );
    }

    return (
        <div>
            {mode !== 'edit' && (
                <button type="button" className="btn btn-link p-0 mb-3" onClick={onBack}>
                    ← Back
                </button>
            )}

            <InstitutionField
                institutionOptions={institutionOptions}
                value={form.institutionId}
                onChange={val => setField('institutionId', val)}
            />
            {submitted && !form.institutionId && err('Institution is required.')}

            {loadingPrograms && (
                <p className="text-muted small mb-2">Loading programs…</p>
            )}

            <ProgramSelector
                inputId="split-source-program"
                label="Program to Split"
                programs={programs}
                value={form.sourceProgramId}
                onChange={id => setField('sourceProgramId', id)}
            />
            {submitted && !form.sourceProgramId && err('Program to split is required.')}

            {sourceProgram && (
                <>
                    <p className="text-muted small font-weight-bold text-uppercase mb-1 mt-2">
                        Original Program
                    </p>
                    <ProgramSummaryCard program={sourceProgram} />
                    <hr />
                </>
            )}

            {sourceProgram && activeProg && (
                <>
                    {/* Multi-program navigation header */}
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <p className="text-muted small font-weight-bold text-uppercase mb-0">
                            New Program from Split
                        </p>
                        <span className="text-muted small">
                            Program {activeIndex + 1} of {newPrograms.length}
                        </span>
                    </div>

                    {newPrograms.length > 1 && (
                        <div className="d-flex mb-3" style={{ gap: '0.5rem' }}>
                            <Button
                                color="link"
                                size="sm"
                                className="p-0"
                                disabled={activeIndex === 0}
                                onClick={() => setActiveIndex(i => i - 1)}
                            >
                                ← Previous
                            </Button>
                            <Button
                                color="link"
                                size="sm"
                                className="p-0"
                                disabled={activeIndex === newPrograms.length - 1}
                                onClick={() => setActiveIndex(i => i + 1)}
                            >
                                Next →
                            </Button>
                            {mode !== 'edit' && (
                                <Button
                                    color="link"
                                    size="sm"
                                    className="p-0 text-danger ml-auto"
                                    onClick={removeActiveProgram}
                                >
                                    Remove this program
                                </Button>
                            )}
                        </div>
                    )}

                    {/* Program Name */}
                    <FormGroup>
                        <Label for={`split-name-${activeIndex}`}>
                            Program Name <sup className="text-danger">*</sup>
                        </Label>
                        <Input
                            id={`split-name-${activeIndex}`}
                            type="text"
                            placeholder="New program name"
                            value={activeProg.programName}
                            onChange={e => updateActiveProg('programName', e.target.value)}
                        />
                        {activeErrors.programName && err(activeErrors.programName)}
                    </FormGroup>

                    {/* CIP Code */}
                    <FormGroup>
                        <Label for={`split-cip-${activeIndex}`}>
                            CIP Code <sup className="text-danger">*</sup>
                        </Label>
                        <Select
                            inputId={`split-cip-${activeIndex}`}
                            options={cipOptions}
                            value={cipOptions.find(o => o.value === activeProg.cipCode) ?? null}
                            onChange={opt => updateActiveProg('cipCode', opt?.value ?? null)}
                            placeholder="Search CIP codes..."
                            isClearable
                            isLoading={loadingCips}
                        />
                        {activeErrors.cipCode && err(activeErrors.cipCode)}
                    </FormGroup>

                    {/* Degree / Award Level */}
                    <FormGroup>
                        <Label for={`split-degree-${activeIndex}`}>
                            Degree / Award Level
                        </Label>
                        <Select
                            inputId={`split-degree-${activeIndex}`}
                            options={degreeLevelOptions}
                            value={degreeLevelOptions.find(o => o.value === activeProg.degreeLevel) ?? null}
                            onChange={opt => updateActiveProg('degreeLevel', opt?.value ?? null)}
                            placeholder="Select degree level..."
                            isClearable
                        />
                    </FormGroup>

                    {/* Delivery Mode + Nature of Online Program */}
                    <FormGroup>
                        <div className="row">
                            <div className="col-md-6">
                                <Label className="d-block">
                                    Delivery Mode <sup className="text-danger">*</sup>
                                </Label>
                                <RadioCheckGroup
                                    onChange={handleDeliveryModeChange}
                                    name="deliveryMode"
                                    value={activeProg.deliveryMode}
                                    options={DELIVERY_MODE_OPTIONS}
                                />
                                {activeErrors.deliveryMode && err(activeErrors.deliveryMode)}
                            </div>
                            {activeDeliveryMode !== null && (
                                <div className="col-md-6 mt-3 mt-md-0">
                                    <Label className="d-block">
                                        Nature of Online Program
                                        {activeDeliveryMode === 3 && (
                                            <sup className="text-danger"> *</sup>
                                        )}
                                    </Label>
                                    {activeDeliveryMode === 1 && (
                                        <div className="form-control-plaintext">None</div>
                                    )}
                                    {activeDeliveryMode === 2 && (
                                        <div className="form-control-plaintext">100% Online</div>
                                    )}
                                    {activeDeliveryMode === 3 && (
                                        <>
                                            <RadioCheckGroup
                                                onChange={handleNatureChange}
                                                name={`onlineNature`}
                                                value={activeProg.onlineNature}
                                                options={ON_CAMPUS_ONLINE_NATURE_OPTIONS}
                                            />
                                            {activeErrors.onlineNature && err(activeErrors.onlineNature)}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </FormGroup>

                    {/* Credit Hours */}
                    <FormGroup>
                        <Label>Credit Hours <sup className="text-danger">*</sup></Label>
                        <div className="d-flex" style={{ gap: '1rem' }}>
                            <div className="flex-fill">
                                <Label for={`split-min-${activeIndex}`} className="small text-muted mb-1">
                                    Min
                                </Label>
                                <Input
                                    id={`split-min-${activeIndex}`}
                                    type="number"
                                    placeholder="Min"
                                    value={activeProg.creditMin}
                                    onChange={e => updateActiveProg('creditMin', e.target.value)}
                                    min="0"
                                />
                            </div>
                            <div className="flex-fill">
                                <Label for={`split-max-${activeIndex}`} className="small text-muted mb-1">
                                    Max
                                </Label>
                                <Input
                                    id={`split-max-${activeIndex}`}
                                    type="number"
                                    placeholder="Max"
                                    value={activeProg.creditMax}
                                    onChange={e => updateActiveProg('creditMax', e.target.value)}
                                    min="0"
                                />
                            </div>
                        </div>
                        {activeErrors.creditHours && err(activeErrors.creditHours)}
                    </FormGroup>

                    {/* Hint when other programs also have errors */}
                    {programsWithErrors.length > 1 && (
                        <div className="alert alert-warning py-2 small">
                            Programs {programsWithErrors.join(', ')} have validation errors. Please fix each before submitting.
                        </div>
                    )}

                    {/* Add another program */}
                    {mode !== 'edit' && <div className="mb-3">
                        <Button
                            color="link"
                            size="sm"
                            className="p-0"
                            onClick={addNewProgram}
                            disabled={submitting}
                        >
                            + Add Another Split Program
                        </Button>
                    </div>}

                    <hr />
                </>
            )}

            {/* Additional Comments — shared across all split programs */}
            <AdditionalCommentsField
                id="split-comments"
                value={form.comments}
                onChange={value => setField('comments', value)}
                maximumLength={maximumAdditionalCommentsLength}
                submitted={submitted}
            />

            {submitError && (
                <div className="alert alert-danger mt-3" role="alert">
                    {submitError}
                </div>
            )}

            <div className="d-flex justify-content-end mt-4">
                <Button color="secondary" className="mr-2" onClick={onBack} disabled={submitting}>
                    Cancel
                </Button>
                <Button color="primary" onClick={handleSubmit} disabled={submitting || !sourceProgram}>
                    {submitting
                        ? mode === 'edit' ? 'Saving…' : 'Submitting…'
                        : mode === 'edit' ? 'Save Changes' : 'Submit Request'}
                </Button>
            </div>
        </div>
    );
};
